import React from "react";
import { Link, useParams } from "react-router-dom";
import { trpc } from "~/lib/trpc";
import { ListView } from "~/components/scan/ListView";
import { formatNumber } from "~/lib/utils";
import { useT } from "~/lib/i18n";

function slugToLabel(slug: string): string {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function MarcheModel(): React.JSX.Element {
  const { brandSlug = "", modelSlug = "" } = useParams();
  const { t, numberLocale } = useT();

  const dealsQ = trpc.fairPrice.topDealsBySlug.useQuery(
    { brandSlug, modelSlug, limit: 48 },
    { enabled: brandSlug.length > 0 && modelSlug.length > 0 },
  );

  const brandName = dealsQ.data?.brand?.name ?? slugToLabel(brandSlug);
  const modelName = dealsQ.data?.model?.name ?? slugToLabel(modelSlug);
  const deals = dealsQ.data?.deals ?? [];

  React.useEffect(() => {
    if (brandName && modelName) {
      document.title = `${brandName} ${modelName} — ${t("nav.market")} — MotorScan`;
    }
    return () => { document.title = "MotorScan — The EU car scanner"; };
  }, [brandName, modelName, t]);

  return (
    <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-12">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-[12px] text-ink-tertiary mb-4 flex-wrap">
        <Link to="/marche" className="hover:text-ink-secondary transition-colors">
          {t("nav.market")}
        </Link>
        <span className="opacity-40">/</span>
        <Link to={`/marche?brand=${brandSlug}`} className="hover:text-ink-secondary transition-colors">
          {brandName}
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-ink-secondary">{modelName}</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl leading-tight text-ink-primary">
            {brandName} {modelName}
          </h1>
          <p className="mt-2 text-sm text-ink-secondary max-w-[55ch]">
            {t("web.market.modelSubtitle")}
          </p>
        </div>
        <Link
          to={`/scan/new?brand=${encodeURIComponent(brandSlug)}&model=${encodeURIComponent(modelSlug)}`}
          className="shrink-0 h-10 px-4 inline-flex items-center justify-center text-sm font-medium rounded-lg bg-accent text-[var(--ink-on-accent)] hover:bg-[var(--accent-hover)] transition-colors"
        >
          {t("web.market.createHunt")}
        </Link>
      </div>

      {!dealsQ.isPending && deals.length > 0 && (
        <p className="mb-4 text-sm text-ink-secondary">
          {t("web.market.listingsCount", { count: formatNumber(deals.length, numberLocale) })}
          {" — "}
          {numberLocale === "fr-FR" ? "triées par meilleure affaire" : "sorted by best deal"}
        </p>
      )}

      <ListView deals={deals} loading={dealsQ.isPending} />

      {!dealsQ.isPending && deals.length === 0 && (
        <div className="mt-8 text-center py-12 rounded-xl border border-[var(--divider-weak)] bg-[var(--surface-sunken)]">
          <p className="text-sm text-ink-secondary">{t("web.market.modelEmpty")}</p>
          <Link
            to={`/scan/new?brand=${encodeURIComponent(brandSlug)}&model=${encodeURIComponent(modelSlug)}`}
            className="mt-4 inline-flex h-10 px-4 items-center text-sm font-medium text-accent hover:underline"
          >
            {t("web.market.createHunt")}
          </Link>
        </div>
      )}
    </div>
  );
}
